import {
  WorkflowBlock,
  WorkflowRun,
  isWorkflowBlock,
  workflowRunBaseOps,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { WriteTransaction } from 'replicache'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'
import { WithServerSecret } from '../../../../tx/spec'
import { updateBlock } from './crud'

const createWorkflowRun = async (
  tx: WriteTransaction,
  {
    data,
    isoNow,
    rootBlockId,
  }: WithServerSecret<{
    data: WorkflowRun
    isoNow: string
    rootBlockId: string
  }>
): Promise<{ workflowRun: WorkflowRun; workflowBlock: WorkflowBlock }> => {
  ensureBaseHubTransaction(tx)

  const treeManager = await tx.getTreeManager(rootBlockId)
  let workflowBlock = treeManager.getNode(data.workflowBlockId, {
    required: true,
  }).value
  invariant(
    isWorkflowBlock(workflowBlock),
    `Expected ${workflowBlock.id} to be a workflow block, but got ${workflowBlock.type}`
  )

  const existingRun = workflowBlock.value.activeRuns?.find((run) => {
    let isEq =
      run.branchId === data.branchId &&
      run.status === 'pending' &&
      run.action.type === data.action.type
    if (!isEq) return false

    switch (run.action.type) {
      case 'ai-autofill':
      case 'ai-autofill-variant': {
        invariant(
          data.action.type === run.action.type,
          `Expected action type to be '${run.action.type}'`
        )
        invariant(
          data.trigger.type === 'layout-block.updated',
          `Expected trigger type to be 'layout-block.updated'`
        )
        invariant(
          run.trigger.type === 'layout-block.updated',
          `Expected trigger type to be 'layout-block.updated'`
        )

        if (run.action.type === 'ai-autofill-variant') {
          isEq = run.action.blockId === data.action.blockId
        } else {
          isEq =
            run.action.blockId === data.action.blockId &&
            run.trigger.data.layoutBlockId === data.trigger.data.layoutBlockId
        }
        break
      }

      case 'webhook': {
        invariant(
          data.action.type === 'webhook',
          `Expected action type to be 'webhook'`
        )
        isEq = run.action.url === data.action.url
        break
      }

      case 'notification':
        isEq = false
        break

      default:
        throw new Error(
          'Intended to create a run with a non-supported action type'
        )
    }

    return isEq
  })

  if (existingRun) {
    // already exists, nothing to do
    const workflowRun = await workflowRunBaseOps.get(tx, existingRun.id)
    invariant(workflowRun, `Workflow run ${existingRun.id} not found.`)
    return { workflowRun: workflowRun!, workflowBlock }
  }

  if (data.status === 'pending' || data.status === 'running') {
    const activeRun: WorkflowBlock['value']['activeRuns'][number] = {
      id: data.id,
      action: data.action,
      trigger: data.trigger,
      branchId: data.branchId,
      status: data.status,
    }

    const path = treeManager.getPath(workflowBlock.id)
    invariant(!!path, 'Path to the workflow block not found.')

    const { updatedBlock } = await updateBlock(tx, {
      data: {
        type: 'workflow',
        value: {
          activeRuns: [...(workflowBlock.value.activeRuns ?? []), activeRun],
        },
      },
      id: workflowBlock.id,
      path,
      instanceChildTarget: workflowBlock.instanceChildTarget,
      isoNow,
      strategy: 'merge-deep',
    })
    invariant(
      isWorkflowBlock(updatedBlock),
      `Expected ${updatedBlock.id} to be a workflow block, but got ${updatedBlock.type}`
    )
    workflowBlock = updatedBlock
  }

  const updatedRun = {
    ...data,
    workflowBlockId: workflowBlock.id,
  }
  await workflowRunBaseOps.set(tx, updatedRun)

  return { workflowRun: updatedRun, workflowBlock }
}

const bulkUpdateWorkflowRuns = async (
  tx: WriteTransaction,
  {
    ids,
    data,
    isoNow,
    rootBlockId,
  }: WithServerSecret<{
    ids: WorkflowRun['id'][]
    data: Partial<Pick<WorkflowRun, 'status' | 'metadata' | 'updatedAt'>>
    isoNow: string
    rootBlockId: string
  }>
) => {
  ensureBaseHubTransaction(tx)

  const output = await Promise.all(
    ids.map(
      async (id) =>
        await updateWorkflowRun(tx, { id, data, isoNow, rootBlockId })
    )
  )

  return output
}

const updateWorkflowRun = async (
  tx: WriteTransaction,
  {
    id,
    data,
    forceUpdateIfStopped = false,
    isoNow,
    rootBlockId,
  }: WithServerSecret<{
    id: WorkflowRun['id']
    data: Partial<Pick<WorkflowRun, 'status' | 'metadata' | 'updatedAt'>>
    forceUpdateIfStopped?: boolean
    isoNow: string
    rootBlockId: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  const workflowRun = await workflowRunBaseOps.get(tx, id)
  invariant(workflowRun, `Workflow run ${id} not found.`)

  if (
    workflowRun.status === 'stopped' &&
    data?.status !== 'stopped' &&
    !forceUpdateIfStopped
  ) {
    throw new Error(
      `Workflow run ${id} is stopped. Cannot update it to ${data.status}.`
    )
  }

  if (
    !['pending', 'running', 'stopped'].includes(workflowRun.status) &&
    data?.status === 'stopped'
  ) {
    throw new Error(
      `Workflow run ${id} is ${workflowRun.status}. Cannot stop it.`
    )
  }

  const treeManager = await tx.getTreeManager(rootBlockId)
  const workflowBlock = treeManager.getNode(workflowRun.workflowBlockId, {
    required: true,
  }).value
  invariant(
    isWorkflowBlock(workflowBlock),
    'Workflow block not found in the tree.'
  )

  const path = treeManager.getPath(workflowBlock.id)
  invariant(!!path, 'Path to the workflow block not found.')

  let newActiveRuns = [...workflowBlock.value.activeRuns]
  const updatedRun = { ...workflowRun, ...data, id }

  if (updatedRun.status === 'pending' || updatedRun.status === 'running') {
    const runIsInActiveRuns = newActiveRuns.some((run) => run.id === id)
    if (!runIsInActiveRuns) {
      newActiveRuns.push({
        ...workflowRun,
        ...data,
        status: updatedRun.status as 'pending' | 'running',
      })
    } else {
      newActiveRuns = newActiveRuns.map((run) => {
        if (run.id === id) {
          return {
            ...run,
            ...data,
            status: updatedRun.status as 'pending' | 'running',
          }
        }

        return run
      })
    }
  } else {
    newActiveRuns = newActiveRuns.filter((run) => run.id !== id)
  }

  const [{ updatedBlock }] = await Promise.all([
    await updateBlock(tx, {
      data: {
        type: 'workflow',
        value: { activeRuns: newActiveRuns },
      },
      id: workflowBlock.id,
      path,
      instanceChildTarget: workflowBlock.instanceChildTarget,
      isoNow,
      strategy: 'merge-deep',
    }),
    workflowRunBaseOps.update(tx, updatedRun),
  ])

  return {
    workflowRun: updatedRun,
    workflowBlock: updatedBlock as WorkflowBlock,
  }
}

const runWorkflows = async (
  tx: WriteTransaction,
  {
    ids,
    isoNow,
    metadata,
    rootBlockId,
  }: {
    ids: WorkflowRun['id'][]
    branchId: string // used in the webhook, do not delete
    isoNow: string
    metadata?: WorkflowRun['metadata']
    rootBlockId: string
  }
) => {
  return await bulkUpdateWorkflowRuns(tx, {
    ids,
    data: { status: 'running', updatedAt: isoNow, metadata },
    isoNow,
    rootBlockId,
  })
}

const stopWorkflowRuns = async (
  tx: WriteTransaction,
  {
    ids,
    isoNow,
    rootBlockId,
    username,
  }: WithServerSecret<{
    ids: WorkflowRun['id'][]
    isoNow: string
    rootBlockId: string
    username: string | undefined | null
  }>
) => {
  ensureBaseHubTransaction(tx)
  const runToWbMap = new Map<WorkflowRun['id'], WorkflowBlock>()
  const treeManager = await tx.getTreeManager(rootBlockId)
  await Promise.all(
    ids.map(async (id) => {
      const run = await workflowRunBaseOps.get(tx, id)
      invariant(run, `WorkflowRun ${id} not found.`)
      const workflowBlock = await treeManager.getNode(run.workflowBlockId)
        ?.value
      invariant(
        workflowBlock,
        `Workflow block ${run.workflowBlockId} not found.`
      )
      invariant(
        isWorkflowBlock(workflowBlock),
        `Workflow run attempted to be created over a ${workflowBlock.type}(id: ${workflowBlock.id}) block instead of a workflow block`
      )

      runToWbMap.set(id, workflowBlock)
    })
  )
  await Promise.all(
    ids.map(async (id) => {
      const wb = runToWbMap.get(id)

      if (wb) {
        const activeRuns = (wb.value.activeRuns ?? []).filter(
          (run) => run.id !== id
        )
        const path = treeManager.getPath(wb.id)
        invariant(!!path, 'Path to the workflow block not found.')

        await updateBlock(tx, {
          id: wb.id,
          data: { value: { activeRuns }, type: 'workflow' },
          instanceChildTarget: wb.instanceChildTarget,
          path,
          strategy: 'merge-deep',
          isoNow,
        })
      }
      await updateWorkflowRun(tx, {
        id,
        data: {
          status: 'stopped',
          metadata: {
            reason: `${username || 'User'} stopped this workflow run`,
            stoppedAt: isoNow,
          },
        },
        forceUpdateIfStopped: true,
        isoNow,
        rootBlockId,
      })
    })
  )
}

export const workflowRunMutators = {
  getWorkflowRun: workflowRunBaseOps.get,
  listWorkflowRuns: workflowRunBaseOps.list,
  createWorkflowRun,
  updateWorkflowRun,
  bulkUpdateWorkflowRuns,
  runWorkflows,
  stopWorkflowRuns,
}
